import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ClipboardList,
  FileText,
  Loader2,
  MessageSquarePlus,
  MessageSquareText,
  Search,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, pdfUrl, type CondominioListItem } from "../lib/api";
import { ChipPagina, DigitarPaginas } from "./PaginaUI";
import { VisualizadorPaginas } from "./VisualizadorPaginas";

const brl = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface ConsultaResp {
  tipo: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dados: any;
}

interface Props {
  onVoltar: () => void;
  /** Quando vem de dentro de um condomínio, a busca começa limitada a ele. */
  condominioId?: number;
}

interface BuscaResultado {
  condominio_id: number;
  condominio_nome: string;
  mes: string;
  pagina: string;
  ocorrencias: number;
  trechos: string[];
  arquivo: string;
  competencia_id?: number | null;
}
interface BuscaResp {
  termo: string;
  total_paginas: number;
  total_ocorrencias: number;
  paginas_varridas?: number;
  resultados: BuscaResultado[];
  aviso?: string;
}
interface Fonte {
  condominio_id: number;
  condominio_nome: string;
  mes: string;
  pagina: string;
  competencia_id?: number | null;
}
interface PerguntaResp {
  resposta: string;
  fontes: Fonte[];
  sem_contexto: boolean;
}

type Aba = "buscar" | "perguntar";

export function AssistentePage({ onVoltar, condominioId }: Props) {
  const [aba, setAba] = useState<Aba>("buscar");
  // Visualizador de páginas (cada resultado pode ser de um mês/competência diferente).
  const [ver, setVer] = useState<{ comp: number; paginas: number[] } | null>(null);
  const [termo, setTermo] = useState("");
  const [termo2, setTermo2] = useState(""); // 2º termo (modo E)
  const [termo3, setTermo3] = useState(""); // 3º termo (modo E)
  const [pergunta, setPergunta] = useState("");
  const [mes, setMes] = useState(""); // filtro opcional 'AAAA-MM'
  const { data: conds } = useQuery<CondominioListItem[]>({
    queryKey: ["condominios"],
    queryFn: () => api.get<CondominioListItem[]>("/condominios"),
  });
  // O Assistente SEMPRE trabalha em UM condomínio (não existe "todos"): vem do
  // foco (quando aberto de dentro de um condomínio) ou de um seletor.
  const [condSel, setCondSel] = useState<number | "">(condominioId ?? "");
  useEffect(() => {
    if (condominioId != null) setCondSel(condominioId);
  }, [condominioId]);
  const condNum = condSel === "" ? null : Number(condSel);
  const condNome = conds?.find((c) => c.id === condNum)?.nome;

  // "Ver páginas do balancete" — escolher um mês do condomínio e abrir as páginas.
  const [verMes, setVerMes] = useState<number | "">("");
  const { data: compsVer } = useQuery<
    { id: number; ano: number; mes: number; periodo_str: string; pdfs: { n_paginas: number | null }[] }[]
  >({
    queryKey: ["competencias", condNum],
    queryFn: () => api.get(`/condominios/${condNum}/competencias`),
    enabled: condNum != null,
  });
  const compVerSel = compsVer?.find((c) => c.id === verMes) ?? null;
  const totalPagVer = compVerSel
    ? compVerSel.pdfs.reduce((a, p) => a + (p.n_paginas ?? 0), 0)
    : 0;

  const mesArg = () => (mes.trim() === "" ? null : mes.trim());

  const buscar = useMutation({
    mutationFn: () =>
      api.post<BuscaResp>("/assistente/buscar", {
        termo,
        termo2: termo2.trim() === "" ? null : termo2.trim(),
        termo3: termo3.trim() === "" ? null : termo3.trim(),
        mes: mesArg(),
        condominio_id: condNum,
      }),
  });
  // ---- Chat multi-turno (aba Perguntar) ----
  // A conversa vive enquanto a tela está aberta. O frontend guarda os turnos e
  // reenvia como "historico" — assim a IA dá seguimento (você pode responder
  // "sim" a uma oferta dela). "Nova conversa" zera (e o custo volta ao mínimo).
  const [conversa, setConversa] = useState<
    { papel: "user" | "assistant"; texto: string; fontes?: Fonte[] }[]
  >([]);
  const perguntar = useMutation({
    mutationFn: (vars: { pergunta: string; historico: { papel: string; texto: string }[] }) =>
      api.post<PerguntaResp>("/assistente/perguntar", {
        pergunta: vars.pergunta,
        historico: vars.historico,
        mes: mesArg(),
        condominio_id: condNum,
      }),
    onSuccess: (d) =>
      setConversa((prev) => [...prev, { papel: "assistant", texto: d.resposta, fontes: d.fontes }]),
    onError: () =>
      setConversa((prev) => [
        ...prev,
        { papel: "assistant", texto: "Não consegui responder agora. Tente de novo.", fontes: [] },
      ]),
  });
  const enviarPergunta = () => {
    const q = pergunta.trim();
    if (q.length < 2 || condNum == null || perguntar.isPending) return;
    const historico = conversa.map((m) => ({ papel: m.papel, texto: m.texto }));
    setConversa((prev) => [...prev, { papel: "user", texto: q }]);
    setPergunta("");
    perguntar.mutate({ pergunta: q, historico });
  };
  const novaConversa = () => {
    setConversa([]);
    setPergunta("");
    perguntar.reset();
  };

  const buscaResp = buscar.data;

  // ---- Consultas rápidas (SEM IA, custo zero) ----
  const [consultaMes, setConsultaMes] = useState("");
  const [consultaResp, setConsultaResp] = useState<ConsultaResp | null>(null);
  // Normaliza o que o usuário digita para "AAAA-MM". Aceita 2026-7, 2026/07,
  // 07/2026 e 07-2026 — assim os botões não ficam travados por causa do formato.
  const mesNormalizado = (() => {
    const t = consultaMes.trim();
    let m = t.match(/^(\d{4})[-/](\d{1,2})$/); // AAAA-MM ou AAAA/M
    if (!m) {
      const inv = t.match(/^(\d{1,2})[-/](\d{4})$/); // MM/AAAA ou M-AAAA
      if (inv) m = [inv[0], inv[2], inv[1]] as unknown as RegExpMatchArray;
    }
    if (!m) return null;
    const ano = m[1];
    const mesN = Number(m[2]);
    if (mesN < 1 || mesN > 12) return null;
    return `${ano}-${String(mesN).padStart(2, "0")}`;
  })();
  const [consultaAviso, setConsultaAviso] = useState<string | null>(null);
  const consultar = useMutation({
    mutationFn: (tipo: string) =>
      api.post<ConsultaResp>("/assistente/consulta", {
        tipo,
        condominio_id: condNum,
        mes: mesNormalizado ?? consultaMes.trim(),
      }),
    onSuccess: (d) => setConsultaResp(d),
  });
  const consultaPronta = condNum != null && mesNormalizado != null;
  // Botões SEMPRE clicáveis: se faltar condomínio ou mês, avisa em vez de ficar
  // "morto". Assim o usuário nunca fica sem entender por que nada acontece.
  const rodarConsulta = (tipo: string) => {
    if (condNum == null) {
      setConsultaResp(null);
      setConsultaAviso("Escolha um condomínio no combo-box acima.");
      return;
    }
    if (mesNormalizado == null) {
      setConsultaResp(null);
      setConsultaAviso("Informe o mês no formato AAAA-MM (ex.: 2026-07).");
      return;
    }
    setConsultaAviso(null);
    consultar.mutate(tipo);
  };

  const renderConsulta = (r: ConsultaResp) => {
    const d = r.dados || {};
    if (d.erro) return <p className="text-sm text-amber-700">{d.erro}</p>;
    if (r.tipo === "inadimplencia" || r.tipo === "inadimplencia_extras") {
      const ehExtras = r.tipo === "inadimplencia_extras";
      const titulo = ehExtras ? "Chamadas Extras (Fundo de Reserva)" : "Cotas condominiais";
      // Mês sem chamada extra é normal — não é inadimplência.
      if (ehExtras && d.tem_fundo_reserva === false) {
        return (
          <div className="text-sm space-y-1">
            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide">{titulo}</p>
            <p className="text-slate-600">{d.periodo} · este mês não teve chamada extra (nenhum aporte ao Fundo de Reserva). Ninguém em dívida.</p>
          </div>
        );
      }
      return (
        <div className="text-sm space-y-1">
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide">{titulo}</p>
          <p className="text-slate-700">{d.periodo} · {d.quitadas}/{d.total_unidades} quitadas</p>
          {d.inadimplentes?.quantidade > 0 ? (
            <p className="text-rose-700"><b>Inadimplentes ({d.inadimplentes.quantidade}):</b> {d.inadimplentes.unidades.join(", ")}</p>
          ) : (
            <p className="text-emerald-700">Nenhuma unidade inadimplente.</p>
          )}
          {d.em_atraso?.quantidade > 0 && (
            <p className="text-amber-700"><b>Em atraso ({d.em_atraso.quantidade}):</b> {d.em_atraso.unidades.join(", ")}</p>
          )}
        </div>
      );
    }
    if (r.tipo === "saldos") {
      return (
        <div className="text-sm">
          <table className="w-full">
            <thead>
              <tr className="text-slate-500 text-[11px] uppercase">
                <th className="text-left font-semibold py-1">Conta</th>
                <th className="text-right font-semibold">Anterior</th>
                <th className="text-right font-semibold">Receitas</th>
                <th className="text-right font-semibold">Despesas</th>
                <th className="text-right font-semibold">Final</th>
              </tr>
            </thead>
            <tbody>
              {(d.contas || []).map((c: any) => (
                <tr key={c.codigo} className="border-t border-slate-100">
                  <td className="py-1 text-slate-700">{c.codigo}</td>
                  <td className="text-right tabular-nums">{brl(c.saldo_anterior)}</td>
                  <td className="text-right tabular-nums text-emerald-700">{brl(c.receitas)}</td>
                  <td className="text-right tabular-nums text-rose-700">{brl(c.despesas)}</td>
                  <td className="text-right tabular-nums font-medium">{brl(c.saldo_final)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-slate-600">Saldo geral: <b>{brl(d.saldo_geral)}</b></p>
        </div>
      );
    }
    if (r.tipo === "despesas") {
      return (
        <div className="text-sm">
          {(d.categorias || []).length === 0 && <p className="text-slate-500">{d.aviso || "Sem despesas."}</p>}
          {(d.categorias || []).map((c: any) => (
            <div key={c.categoria} className="flex justify-between border-t border-slate-100 py-0.5">
              <span className="text-slate-700">{c.categoria}</span>
              <span className="tabular-nums">{brl(c.valor)} {c.percentual != null && <span className="text-slate-400">({c.percentual}%)</span>}</span>
            </div>
          ))}
          {d.total != null && <p className="mt-1 text-slate-600">Total: <b>{brl(d.total)}</b></p>}
        </div>
      );
    }
    if (r.tipo === "fiscal") {
      const notas = d.notas || [];
      return (
        <div className="text-sm space-y-1">
          {notas.length === 0 && <p className="text-slate-500">{d.resumo?.aviso || "Nenhuma NF neste mês."}</p>}
          {notas.map((n: any, i: number) => (
            <div key={i} className="border-t border-slate-100 py-1">
              <span className="font-medium text-slate-800">NF {n.numero}</span>
              <span className="text-slate-500"> · {n.prestador || "—"} · {brl(n.valor_servico)}</span>
              <div className="text-[12px] text-slate-600">
                Federal: <b>{n.federal?.status}</b> ({brl(n.federal?.valor_retido)}) · Municipal: <b>{n.municipal?.status}</b> ({brl(n.municipal?.valor_retido)})
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (r.tipo === "findings") {
      const fs = d.findings || [];
      if (fs.length === 0) return <p className="text-sm text-emerald-700">Nenhuma divergência na auditoria deste mês.</p>;
      return (
        <div className="text-sm space-y-1">
          {fs.map((f: any, i: number) => (
            <div key={i} className="border-t border-slate-100 py-1">
              <span className={f.severidade === "FAIL" ? "text-rose-700 font-semibold" : "text-amber-700 font-semibold"}>{f.severidade}</span>
              <span className="text-slate-500"> {f.validacao}</span>
              <div className="text-[12px] text-slate-700">{f.mensagem}</div>
            </div>
          ))}
        </div>
      );
    }
    return <pre className="text-xs text-slate-500">{JSON.stringify(d, null, 2)}</pre>;
  };

  return (
    <div>
      {ver && (
        <VisualizadorPaginas
          competenciaId={ver.comp}
          paginas={ver.paginas}
          aberto={true}
          onFechar={() => setVer(null)}
        />
      )}
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Assistente</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Busque um termo nas páginas dos balancetes ou pergunte em português. As
        respostas se apoiam nas páginas já lidas (OCR) e citam a origem.
      </p>

      <div className="flex items-center gap-2 mb-4 text-sm">
        <span className="text-slate-600">Condomínio:</span>
        {condominioId != null ? (
          <span className="inline-flex items-center gap-1.5 text-slate-800 bg-brand-50 border border-brand-100 rounded-full px-3 py-1 font-medium">
            <Sparkles size={13} className="text-brand-700" />
            {condNome ?? "este condomínio"}
          </span>
        ) : (
          <select
            value={condSel}
            onChange={(e) => setCondSel(e.target.value === "" ? "" : Number(e.target.value))}
            className={
              mesNormalizado != null && condNum == null
                ? "px-2 py-1.5 border-2 border-rose-400 ring-2 ring-rose-200 rounded-md bg-rose-50 text-rose-800 font-medium"
                : "px-2 py-1.5 border border-slate-300 rounded-md bg-white"
            }
          >
            <option value="">Escolha um condomínio…</option>
            {conds?.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        )}
      </div>

      {/* Ver páginas do balancete (escolher mês → abrir páginas/PDF) */}
      {condNum != null && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 mb-5">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={16} className="text-brand-700" />
            <h3 className="text-sm font-semibold text-slate-900">Ver páginas do balancete</h3>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            Escolha o mês e veja as páginas (ex.: 2;7;9 ou 5-9) ou abra o PDF original.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={verMes}
              onChange={(e) => setVerMes(e.target.value === "" ? "" : Number(e.target.value))}
              className="px-2 py-1.5 text-sm border border-slate-300 rounded-md bg-white"
            >
              <option value="">Mês…</option>
              {compsVer?.map((c) => (
                <option key={c.id} value={c.id}>{c.periodo_str}</option>
              ))}
            </select>
            {compVerSel && (
              <>
                <DigitarPaginas
                  max={totalPagVer || undefined}
                  onAbrir={(ps) => setVer({ comp: compVerSel.id, paginas: ps })}
                />
                <a
                  href={pdfUrl(compVerSel.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:border-slate-400"
                >
                  <FileText size={13} /> Abrir PDF
                </a>
              </>
            )}
          </div>
          {compsVer && compsVer.length === 0 && (
            <p className="text-[11px] text-slate-400 mt-2">Nenhum mês cadastrado ainda.</p>
          )}
        </div>
      )}

      {/* Consultas rápidas (SEM IA, custo zero) */}
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 shadow-sm p-4 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList size={16} className="text-emerald-700" />
          <h3 className="text-sm font-semibold text-slate-900">Consultas rápidas</h3>
          <span className="text-[10px] font-semibold text-emerald-700 bg-white border border-emerald-200 rounded-full px-2 py-0.5">
            Custo Zero
          </span>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Respostas direto do banco, sem usar a IA. Informe o mês e clique.
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">Mês:</span>
          <input
            value={consultaMes}
            onChange={(e) => setConsultaMes(e.target.value)}
            placeholder="AAAA-MM"
            className="w-32 px-2 py-1.5 text-sm border border-slate-300 rounded-md"
          />
        </div>
        {mesNormalizado != null && condNum == null && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border-2 border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800">
            <AlertCircle size={16} className="flex-shrink-0" />
            Escolha um condomínio no combo-box acima.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {([
            ["inadimplencia", "Inadimplência (Cotas)"],
            ["inadimplencia_extras", "Inadimplência (Chamadas Extras)"],
            ["fiscal", "Impostos"],
            ["saldos", "Saldos"],
            ["despesas", "Despesas"],
            ["findings", "Auditoria"],
          ] as [string, string][]).map(([tipo, label]) => (
            <button
              key={tipo}
              type="button"
              disabled={consultar.isPending}
              onClick={() => rodarConsulta(tipo)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md bg-white border hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-40 ${consultaPronta ? "border-slate-300" : "border-slate-200 text-slate-500"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {consultaAviso && (
          <p className="text-[11px] text-amber-700 mt-2">{consultaAviso}</p>
        )}
        {consultar.isPending && <p className="text-xs text-slate-400 mt-3">consultando…</p>}
        {consultaResp && !consultar.isPending && (
          <div className="mt-3 bg-white border border-slate-200 rounded-xl p-3">
            {renderConsulta(consultaResp)}
          </div>
        )}
      </div>

      {/* Abas */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 mb-4">
        <button
          type="button"
          onClick={() => setAba("buscar")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md ${
            aba === "buscar" ? "bg-brand-700 text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Search size={15} /> Buscar nas páginas
          <span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            Custo Zero
          </span>
        </button>
        <button
          type="button"
          onClick={() => setAba("perguntar")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md ${
            aba === "perguntar" ? "bg-brand-700 text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <MessageSquareText size={15} /> Perguntar
          <span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
            Com Custo
          </span>
        </button>
      </div>

      {/* Filtro de mês (opcional), comum às duas abas */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-slate-500">Filtrar por mês (opcional):</span>
        <input
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          placeholder="AAAA-MM (ex.: 2026-05)"
          className="w-40 px-2 py-1 text-sm border border-slate-300 rounded-md"
        />
      </div>

      {aba === "buscar" ? (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <input
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && termo.trim().length >= 2 && condNum != null && buscar.mutate()}
              placeholder="Termo (ex.: 5044)"
              className="w-40 px-3 py-2 text-sm border border-slate-300 rounded-md"
            />
            <span className="text-xs text-slate-400">e</span>
            <input
              value={termo2}
              onChange={(e) => setTermo2(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && termo.trim().length >= 2 && condNum != null && buscar.mutate()}
              placeholder="2º (opcional, ex.: NF)"
              className="w-40 px-3 py-2 text-sm border border-slate-300 rounded-md"
            />
            <span className="text-xs text-slate-400">e</span>
            <input
              value={termo3}
              onChange={(e) => setTermo3(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && termo.trim().length >= 2 && condNum != null && buscar.mutate()}
              placeholder="3º (opcional)"
              className="w-32 px-3 py-2 text-sm border border-slate-300 rounded-md"
            />
            <button
              type="button"
              onClick={() => buscar.mutate()}
              disabled={buscar.isPending || termo.trim().length < 2 || condNum == null}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white text-sm font-medium rounded-md"
            >
              {buscar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              Buscar
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mb-4">
            Preenchendo mais de um termo, só aparecem páginas que contêm <b>todos</b> —
            ex.: “NF” + “5044” evita números que não são de nota. Use até 3 para refinar.
          </p>

          {buscaResp && (
            <p className="text-xs text-slate-500 mb-3">
              {buscaResp.aviso
                ? buscaResp.aviso
                : `${buscaResp.total_paginas} página(s) · ${buscaResp.total_ocorrencias} ocorrência(s)` +
                  (buscaResp.paginas_varridas ? ` · ${buscaResp.paginas_varridas} páginas varridas` : "")}
            </p>
          )}

          <div className="space-y-2">
            {buscaResp?.resultados.map((r) => (
              <div
                key={`${r.condominio_id}-${r.mes}-${r.pagina}`}
                className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <FileText size={13} className="text-brand-700" />
                  <span className="font-medium text-slate-700">{r.condominio_nome}</span>
                  <span>· {r.mes} ·</span>
                  {r.competencia_id != null ? (
                    <ChipPagina
                      fonte={r.pagina}
                      onAbrir={(p) => setVer({ comp: r.competencia_id!, paginas: [p] })}
                    />
                  ) : (
                    <span>{r.pagina}</span>
                  )}
                  <span className="ml-auto text-brand-700 font-medium">{r.ocorrencias}x</span>
                </div>
                {r.trechos.map((t, i) => (
                  <p key={i} className="text-[13px] text-slate-700 leading-snug">
                    {t}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {buscaResp && buscaResp.resultados.length === 0 && !buscaResp.aviso && (
            <p className="text-sm text-slate-400 py-6 text-center">
              Nada encontrado. Lembre: a busca só cobre páginas já processadas (OCR).
            </p>
          )}
        </div>
      ) : (
        <div>
          {/* Cabeçalho da conversa + Nova conversa */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-slate-400">
              Conversa: você pode responder “sim” e continuar. Cada resposta usa a
              API (entra no Uso &amp; Custos); conversas longas custam mais.
            </p>
            {conversa.length > 0 && (
              <button
                type="button"
                onClick={novaConversa}
                className="inline-flex items-center gap-1.5 whitespace-nowrap ml-3 px-3 py-1.5 rounded-lg border border-brand-300 bg-brand-50 text-brand-700 text-xs font-semibold shadow-sm hover:bg-brand-100 hover:border-brand-400 transition-colors"
                title="Apaga a conversa atual e começa do zero (zera o contexto e o custo)"
              >
                <MessageSquarePlus size={15} />
                Nova conversa
              </button>
            )}
          </div>

          {/* Lista de mensagens */}
          {conversa.length > 0 && (
            <div className="space-y-2 mb-3">
              {conversa.map((m, idx) =>
                m.papel === "user" ? (
                  <div key={idx} className="flex justify-end">
                    <div className="max-w-[85%] bg-brand-700 text-white rounded-2xl rounded-br-sm px-3 py-2 text-[14px] whitespace-pre-wrap leading-relaxed">
                      {m.texto}
                    </div>
                  </div>
                ) : (
                  <div key={idx} className="flex justify-start">
                    <div className="max-w-[85%] bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm">
                      <p className="text-[14px] text-slate-800 whitespace-pre-wrap leading-relaxed">
                        {m.texto}
                      </p>
                      {m.fontes && m.fontes.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                            Fontes (páginas)
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {m.fontes.map((f, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
                              >
                                <FileText size={11} />
                                {f.condominio_nome} · {f.mes} ·{" "}
                                {f.competencia_id != null ? (
                                  <ChipPagina
                                    fonte={f.pagina}
                                    onAbrir={(p) => setVer({ comp: f.competencia_id!, paginas: [p] })}
                                  />
                                ) : (
                                  f.pagina
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
              {perguntar.isPending && (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-slate-500 shadow-sm">
                    <Loader2 size={14} className="animate-spin" /> pensando…
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Caixa de digitar (fixa; não some depois da resposta) */}
          <div className="flex flex-col gap-2">
            <textarea
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  enviarPergunta();
                }
              }}
              rows={conversa.length > 0 ? 2 : 3}
              placeholder={
                conversa.length === 0
                  ? "Ex.: A NF 7749 foi paga? Quais páginas falam de ISSQN em maio?"
                  : "Escreva sua resposta ou próxima pergunta (ex.: sim, pesquise isso)…"
              }
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md resize-y"
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={enviarPergunta}
                disabled={perguntar.isPending || pergunta.trim().length < 2 || condNum == null}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white text-sm font-medium rounded-md"
              >
                {perguntar.isPending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <MessageSquareText size={15} />
                )}
                {conversa.length === 0 ? "Perguntar" : "Enviar"}
              </button>
              {condNum == null && (
                <span className="text-[11px] text-amber-700">Escolha um condomínio acima.</span>
              )}
              <span className="text-[11px] text-slate-400 ml-auto">Enter envia · Shift+Enter quebra linha</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 mt-3">
            As respostas se baseiam no texto lido das páginas (pode conter erros de
            leitura); confira sempre nas fontes citadas.
          </p>
        </div>
      )}
    </div>
  );
}
// Lupa de páginas nos resultados de busca e nas fontes do perguntar — 0.1.6
